import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import FinancialService, {
  ExportCopartPreviewCSVFileStatusEnum,
} from '@services/FinancialService';

import { toastifyApiErrors } from '@utils';
import { useAuth } from '@hooks';

import {
  EXPORT_COPART_PREVIEW_FILE_STATE,
  GET_EXPORT_COPART_PREVIEW_FILE_STATE,
} from './constants/queryKeys';
import { EXPORT_POLLING_INTERVAL_MS } from './constants/interval';

export function useExportCopartPreviewFile() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedCompany, selectedPartnerCompany } = useAuth();
  const [
    isFetchingExportCopartPreviewFile,
    setIsFetchingExportCopartPreviewFile,
  ] = useState(false);
  const financialService = new FinancialService();

  const companyId = selectedCompany?.id ?? '';
  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const fetchExportCopartPreviewFile = async () => {
    setIsFetchingExportCopartPreviewFile(true);
    await queryClient.fetchQuery({
      queryKey: [EXPORT_COPART_PREVIEW_FILE_STATE, companyId],
      cacheTime: 0,
      queryFn: () =>
        financialService.exportCopartPreviewCSVFile({ partnerCompany }),
    });
    setIsFetchingExportCopartPreviewFile(false);
  };

  const {
    isFetching,
    refetch,
    data: exportCopartPreviewFileData,
  } = useQuery({
    queryKey: [GET_EXPORT_COPART_PREVIEW_FILE_STATE, companyId],
    queryFn: () => financialService.getExportCopartPreviewCSVFile(),
    cacheTime: 0,
    onSuccess: success => {
      if (success.status === ExportCopartPreviewCSVFileStatusEnum.ERROR) {
        toastifyApiErrors(success, enqueueSnackbar);
      }
    },
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
    refetchInterval: data =>
      data?.status === ExportCopartPreviewCSVFileStatusEnum.PROCESSING
        ? EXPORT_POLLING_INTERVAL_MS
        : false,
  });

  const isDefaultState =
    exportCopartPreviewFileData?.status ===
      ExportCopartPreviewCSVFileStatusEnum.PROCESS && !isFetching;

  const isExportingCopartPreviewFile =
    exportCopartPreviewFileData?.status ===
      ExportCopartPreviewCSVFileStatusEnum.PROCESSING ||
    isFetching ||
    isFetchingExportCopartPreviewFile;

  const isReadyToDownload =
    exportCopartPreviewFileData?.status ===
      ExportCopartPreviewCSVFileStatusEnum.PROCESSED && !isFetching;

  const handleDownloadCopartPreviewFile = () => {
    if (isReadyToDownload) {
      window.open(exportCopartPreviewFileData?.downloadLink, '_blank');
      queryClient.removeQueries({
        queryKey: [EXPORT_COPART_PREVIEW_FILE_STATE],
      });
    }
  };

  const handleStartDownloadCopartPreviewFile = async () => {
    try {
      if (isDefaultState) await fetchExportCopartPreviewFile();

      refetch();
    } catch (error) {
      toastifyApiErrors(error, enqueueSnackbar);
    }
  };

  const handleExportCopartPreviewFile = async () => {
    handleStartDownloadCopartPreviewFile();
  };

  return {
    handleExportCopartPreviewFile,
    handleDownloadCopartPreviewFile,
    isExportingCopartPreviewFile,
    isReadyToDownload,
  };
}
